/**
 * Username prefix helpers — keep in sync with backend/utils/usernameGenerator.js
 */

export const MAX_USERNAME_LEN = 15;
export const SUFFIX_GLUED_LEN = 6;
export const SUFFIX_UNDERSCORE_LEN = 7;
export const MAX_PREFIX_LEN = MAX_USERNAME_LEN - SUFFIX_GLUED_LEN; // 9

export function sanitizeUsernamePrefixPart(part) {
  return String(part || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, MAX_PREFIX_LEN);
}

export function clampUsernamePrefixInput(raw) {
  return String(raw ?? '').split('\n').map((line) => {
    if (line.includes(',')) {
      return line.split(',').map((seg) => sanitizeUsernamePrefixPart(seg)).join(',');
    }
    return line.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, MAX_PREFIX_LEN);
  }).join('\n');
}

export function parseUsernamePrefixes(raw) {
  const seen = new Set();
  const out = [];
  for (const part of String(raw || '').split(/[\n,]+/)) {
    const sanitized = sanitizeUsernamePrefixPart(part);
    if (!sanitized || seen.has(sanitized)) continue;
    seen.add(sanitized);
    out.push(sanitized);
  }
  return out;
}

function prefixAllowsUnderscore(prefix) {
  return String(prefix || '').length + SUFFIX_UNDERSCORE_LEN <= MAX_USERNAME_LEN;
}

function buildUsername(prefix, underscore) {
  const d = '4635';
  const L = 'yo';
  const useUnder = Boolean(underscore) && prefixAllowsUnderscore(prefix);
  return useUnder ? `${prefix}${d}_${L}` : `${prefix}${d}${L}`;
}

export function exampleUsernamesFromPrefix(raw, limit = 6) {
  const prefixes = parseUsernamePrefixes(raw);
  if (!prefixes.length) return [];
  const out = [];
  for (const prefix of prefixes) {
    out.push(buildUsername(prefix, false));
    if (prefixAllowsUnderscore(prefix)) {
      out.push(buildUsername(prefix, true));
    }
    if (out.length >= limit) break;
  }
  return out.slice(0, limit);
}

export function exampleUsernameFromPrefix(prefix) {
  return exampleUsernamesFromPrefix(prefix, 1)[0] || '';
}

export const USERNAME_PREFIX_HINT =
  `One prefix per line, max ${MAX_PREFIX_LEN} characters each. X usernames cap at ${MAX_USERNAME_LEN}; the random suffix needs ${SUFFIX_GLUED_LEN} or ${SUFFIX_UNDERSCORE_LEN} characters, so a ${MAX_PREFIX_LEN}-character prefix only uses digits+letters (no underscore).`;
